// client/src/components/templates/mensagens/TemplateMensagemForm.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useMutateTemplateMensagem, useTemplateMensagemById } from '../../../hooks/useTemplateMensagem';
import useAlert from '../../../hooks/useAlert';
import { useAuth } from '../../../hooks/useAuth';
import { isMatrizAdmin as checkIsMatrizAdmin } from '../../../utils/permissions';
import { Save, XCircle, Loader2, Code, Eye, ClipboardCopy, ChevronDown } from 'lucide-react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import PropTypes from 'prop-types';
import PeriodoEnvioFields from '../../comunicacao/shared/PeriodoEnvioFields';
import GerarTextoIABox from '../../ai/GerarTextoIABox';

const PERIODO_ENVIO_PADRAO = { momento: 'imediato', offsetMinutos: 0, diasSemana: [], janelaHorario: { inicio: null, fim: null } };

// Componente para o painel de variáveis
const PlaceholdersPanel = ({ onInsert }) => {
    const placeholderGroups = [
        {
            category: 'Dados do Cliente',
            items: [
                { label: 'Nome Completo', value: '{{cliente.nomeCompleto}}' },
                { label: 'Primeiro Nome', value: '{{cliente.primeiroNome}}' },
                { label: 'Email', value: '{{cliente.emailPrincipal}}' },
                { label: 'Telefone', value: '{{cliente.telefonePrincipal}}' }
            ]
        },
        {
            category: 'Dados do Agendamento',
            items: [
                { label: 'Data do Agendamento', value: '{{agendamento.data}}' },
                { label: 'Hora do Agendamento', value: '{{agendamento.hora}}' },
                { label: 'Serviço Agendado', value: '{{agendamento.servico}}' },
                { label: 'Profissional', value: '{{agendamento.profissional}}' }
            ]
        },
        {
            category: 'Dados da Sua Empresa',
            items: [
                { label: 'Nome da Sua Empresa', value: '{{empresa.name}}' },
                { label: 'Telefone da Empresa', value: '{{empresa.telefone}}' }
            ]
        },
        {
            category: 'Dados da Unidade',
            items: [
                { label: 'Nome da Unidade', value: '{{unidade.nome}}' },
                { label: 'Endereço da Unidade', value: '{{unidade.endereco}}' },
                { label: 'Telefone da Unidade', value: '{{unidade.telefone}}' }
            ]
        }
    ];

    const [searchTerm, setSearchTerm] = useState('');
    const filteredGroups = placeholderGroups
        .map(group => ({
            ...group,
            items: group.items.filter(item =>
                item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.value.toLowerCase().includes(searchTerm.toLowerCase())
            )
        }))
        .filter(group => group.items.length > 0);

    return (
        <div className="editor-variables-panel">
            <div className="panel-header">
                <h5 className="panel-title">Variáveis Disponíveis</h5>
                <input 
                    type="search" 
                    placeholder="Buscar variável..." 
                    className="variables-search-input" 
                    value={searchTerm} 
                    onChange={e => setSearchTerm(e.target.value)} 
                />
            </div>
            {filteredGroups.length > 0 ? filteredGroups.map(group => (
                <div key={group.category} className="variables-category">
                    <h6 className="variables-category-title">{group.category}</h6>
                    <div className="variables-grid">
                        {group.items.map(p => (
                            <button 
                                key={p.value} 
                                type="button" 
                                onClick={() => onInsert(p.value, 'editor')} 
                                className="variable-button"
                            >
                                <span className="variable-label">{p.label}</span>
                                <code className="variable-code">{p.value}</code>
                            </button>
                        ))}
                    </div>
                </div>
            )) : (
                <p className="text-muted text-center p-4">Nenhuma variável encontrada.</p>
            )}
        </div>
    );
};

PlaceholdersPanel.propTypes = {
    onInsert: PropTypes.func.isRequired,
};

const TemplateMensagemForm = ({ current, onCloseForm, canalSelecionado, canalOptions = [] }) => {
    const { user } = useAuth();
    const { setAlert } = useAlert();
    const {
        addTemplateMensagem,
        updateTemplateMensagem,
        isPendingMensagem: isMutating
    } = useMutateTemplateMensagem();

    const templateId = current?._id;

    const {
        data: fetchedTemplate,
        isLoading: isLoadingDetails,
        isError: isErrorDetails,
        error: detailsError
    } = useTemplateMensagemById(templateId, {
        enabled: !!templateId
    });
    
    const isMatrizAdmin = checkIsMatrizAdmin(user) && !user?.account && !user?.franquia;

    const getInitialState = useCallback(() => ({
        nome: '',
        canal: canalSelecionado || (canalOptions.length > 0 ? canalOptions[0].value : ''),
        conteudo: '',
        descricao: '',
        categoria: 'Notificação',
        tags: [],
        ativo: true,
        isPadrao: false, // Adicionado ao estado inicial
        restritoMatriz: false,
        periodoEnvioPadrao: PERIODO_ENVIO_PADRAO,
    }), [canalSelecionado, canalOptions]);

    const [template, setTemplate] = useState(() => getInitialState());
    const [viewMode, setViewMode] = useState('editor');
    const [showVariables, setShowVariables] = useState(false);
    const editorRef = useRef(null);
    const sourceEditorRef = useRef(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (current && fetchedTemplate) {
            const loadedTemplate = {
                _id: fetchedTemplate._id,
                nome: fetchedTemplate.nome || '',
                canal: fetchedTemplate.canal || canalSelecionado || '',
                conteudo: fetchedTemplate.conteudo || '',
                descricao: fetchedTemplate.descricao || '',
                categoria: fetchedTemplate.categoria || 'Notificação',
                tags: Array.isArray(fetchedTemplate.tags) ? fetchedTemplate.tags : [],
                ativo: fetchedTemplate.ativo !== undefined ? fetchedTemplate.ativo : true,
                isPadrao: fetchedTemplate.isPadrao || false,
                restritoMatriz: fetchedTemplate.restritoMatriz || false,
                periodoEnvioPadrao: fetchedTemplate.periodoEnvioPadrao || PERIODO_ENVIO_PADRAO,
            };
            setTemplate(loadedTemplate);
        } else if (current && isErrorDetails) {
            console.error('❌ Erro ao carregar detalhes do template:', detailsError);
            setAlert('Erro ao carregar os detalhes do template para edição. ' + (detailsError?.message || ''), 'danger');
        } else if (!current) {
            setTemplate(getInitialState());
        }
        setErrors({});
    }, [current, fetchedTemplate, isErrorDetails, detailsError, getInitialState, canalSelecionado, setAlert]);

    const onEditorReady = useCallback(editor => {
        editorRef.current = editor;

        if (!current && editor.getData() === '') {
            const defaultContent = `<p>Olá {{cliente.primeiroNome}},</p><p>Sua consulta de {{agendamento.servico}} com {{agendamento.profissional}} está agendada para {{agendamento.data}} às {{agendamento.hora}}.</p><p>Aguardamos você!</p><p>Atenciosamente,<br>{{empresa.name}}</p>`;
            editor.setData(defaultContent);
            setTemplate(prev => ({ ...prev, conteudo: defaultContent }));
        }
    }, [current]);

    useEffect(() => {
        if (viewMode === 'editor' && editorRef.current) {
            const editorData = editorRef.current.getData();
            if (editorData !== template.conteudo) {
                editorRef.current.setData(template.conteudo || '');
            }
        }
    }, [viewMode, template.conteudo]);

    const onChange = e => {
        const { name, value } = e.target;
        setTemplate(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const onArrayChange = (e) => {
        const { name, value } = e.target;
        setTemplate(prev => ({
            ...prev,
            [name]: value.split(',').map(item => item.trim()).filter(item => item)
        }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const onCKEditorChange = (event, editor) => {
        setTemplate(prev => ({ ...prev, conteudo: editor.getData() }));
        setErrors(prev => ({ ...prev, conteudo: '' }));
    };

    const onCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setTemplate(prev => ({ ...prev, [name]: checked }));
    };

    const onPeriodoEnvioChange = (novoPeriodo) => {
        setTemplate(prev => ({ ...prev, periodoEnvioPadrao: novoPeriodo }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!template.nome.trim()) {
            newErrors.nome = 'O nome do template é obrigatório.';
        }
        if (!template.conteudo.trim()) {
            newErrors.conteudo = 'O conteúdo da mensagem é obrigatório.';
        }
        if (!template.canal || template.canal === '') {
            newErrors.canal = 'O canal da mensagem é obrigatório.';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onSubmit = async e => {
        e.preventDefault();

        if (!validateForm()) {
            setAlert('Por favor, corrija os erros no formulário.', 'danger');
            return;
        }

        const payload = {
            ...template,
            tags: Array.isArray(template.tags) ? template.tags : []
        };

        if (payload.tipo !== undefined) {
            delete payload.tipo;
        }

        if (!current) {
            delete payload._id;
        }
        
        if (!isMatrizAdmin) {
            delete payload.isPadrao;
            delete payload.restritoMatriz;
        }

        try {
            if (current) {
                await updateTemplateMensagem.mutateAsync({
                    id: template._id,
                    templateData: payload
                });
            } else {
                await addTemplateMensagem.mutateAsync(payload);
            }
            onCloseForm();
        } catch (error) {
            console.error("❌ Erro ao salvar template de mensagem:", error);
            const errorMessage = error.response?.data?.error || error.message || 'Erro desconhecido.';
            setAlert(`Erro ao salvar template: ${errorMessage}`, 'danger');
        }
    };

    const handleClose = () => {
        onCloseForm();
    };

    const insertPlaceholder = useCallback((text, mode) => {
        if (mode === 'editor') {
            const editor = editorRef.current;
            if (editor) {
                try {
                    editor.model.change(writer => {
                        const insertPosition = editor.model.document.selection.getFirstPosition();
                        writer.insertText(text, insertPosition);
                    });
                    editor.editing.view.focus();
                    setShowVariables(false);
                } catch (error) {
                    console.error('Erro ao inserir placeholder no editor visual:', error);
                    setAlert('Erro ao inserir variável no editor visual. Tente novamente.', 'warning');
                }
            } else {
                setAlert('Editor visual não está pronto. Tente novamente.', 'warning');
            }
        } else if (mode === 'source') {
            const textarea = sourceEditorRef.current;
            if (textarea) {
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const newText = template.conteudo.substring(0, start) + text + template.conteudo.substring(end);

                setTemplate(prev => ({
                    ...prev,
                    conteudo: newText
                }));

                setTimeout(() => {
                    textarea.focus();
                    textarea.selectionEnd = start + text.length;
                }, 0);
                setShowVariables(false);
            } else {
                setAlert('Editor de código HTML não está pronto. Tente novamente.', 'warning');
            }
        } else {
            setAlert('Modo de edição inválido para inserir variáveis.', 'warning');
        }
    }, [template.conteudo, setAlert]);

    if (current && isLoadingDetails) {
        return (
            <div className="flex justify-center items-center p-8 bg-white shadow-lg rounded-lg min-h-[300px]">
                <Loader2 className="animate-spin text-blue-600 mr-3" size={32} />
                <span className="text-lg text-gray-700">Carregando template para edição...</span>
            </div>
        );
    }

    if (current && isErrorDetails) {
        return (
            <div className="flex flex-col justify-center items-center p-8 text-red-600 bg-red-50 border border-red-200 rounded-lg min-h-[300px]">
                <XCircle className="mb-4" size={40} />
                <span className="text-lg font-medium text-center">
                    Não foi possível carregar o template.
                    <br />
                    <span className="text-sm text-red-500">
                        {detailsError?.message || 'Erro desconhecido. Verifique a conexão ou tente novamente mais tarde.'}
                    </span>
                </span>
                <button onClick={handleClose} className="mt-6 px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors">
                    Fechar
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={onSubmit} noValidate>
                    <div className="form-header">
                        <h3 className="form-title">
                            {current ? 'Editar' : 'Novo'} Template de Mensagem
                        </h3>
                        <button
                            type="button"
                            onClick={handleClose}
                            title="Fechar"
                            className="form-close-button"
                        >
                            <XCircle size={24} />
                        </button>
                    </div>

                    <fieldset className="form-section">
                        <legend className="form-section-title">Informações Gerais</legend>
                        <div className="form-grid-container">
                            <div className="form-group">
                                <label htmlFor="nome">Nome*</label>
                                <input
                                    type="text"
                                    id="nome"
                                    name="nome"
                                    value={template.nome || ''}
                                    onChange={onChange}
                                    required
                                    className={`form-input ${errors.nome ? 'border-red-500' : ''}`}
                                    placeholder="Ex: Confirmação de Agendamento"
                                />
                                {errors.nome && <p className="text-red-500 text-xs mt-1">{errors.nome}</p>}
                            </div>

                            {!current ? (
                                <div className="form-group">
                                    <label htmlFor="canal">Canal *</label>
                                    <select
                                        id="canal"
                                        name="canal"
                                        value={template.canal || ''}
                                        onChange={onChange}
                                        required
                                        className={`form-input ${errors.canal ? 'border-red-500' : ''}`}
                                    >
                                        <option value="" disabled>Selecione um canal</option>
                                        {canalOptions && canalOptions.length > 0 ? (
                                            canalOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))
                                        ) : (
                                            <option value="">Nenhum canal disponível</option>
                                        )}
                                    </select>
                                    {errors.canal && <p className="text-red-500 text-xs mt-1">{errors.canal}</p>}
                                    <small className="form-help">
                                        O canal não pode ser alterado após a criação do template
                                    </small>
                                </div>
                            ) : (
                                <div className="form-group">
                                    <label>Canal</label>
                                    <div className="readonly-field">
                                        {template.canal || 'Não definido'}
                                    </div>
                                    <small className="form-help">
                                        O canal não pode ser alterado após a criação
                                    </small>
                                </div>
                            )}

                            <div className="form-group full-width">
                                <label htmlFor="descricao">Descrição</label>
                                <textarea
                                    id="descricao"
                                    name="descricao"
                                    value={template.descricao || ''}
                                    onChange={onChange}
                                    rows="2"
                                    className="form-input"
                                    placeholder="Breve descrição do propósito deste template"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="categoria">Categoria</label>
                                <select
                                    id="categoria"
                                    name="categoria"
                                    value={template.categoria || 'Notificação'}
                                    onChange={onChange}
                                    className="form-input"
                                >
                                    <option value="Marketing">Marketing</option>
                                    <option value="Notificação">Notificação</option>
                                    <option value="Transacional">Transacional</option>
                                    <option value="Suporte">Suporte</option>
                                    <option value="Outros">Outros</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="tags">Tags</label>
                                <input
                                    id="tags"
                                    type="text"
                                    name="tags"
                                    value={Array.isArray(template.tags) ? template.tags.join(', ') : ''}
                                    onChange={onArrayChange}
                                    className="form-input"
                                    placeholder="Ex: agendamento,confirmacao,lembrete"
                                />
                                <small className="form-help">
                                    Separe as tags por vírgula
                                </small>
                            </div>
                        </div>
                    </fieldset>

                    <fieldset className="form-section">
                        <legend className="form-section-title">Conteúdo da Mensagem *</legend>
                        <GerarTextoIABox
                            endpoint="/templates/mensagem/gerar-ia"
                            extraPayload={{ canal: template.canal }}
                            disabled={!template.canal}
                            disabledHint="Selecione um canal acima antes de gerar."
                            placeholder='Descreva a mensagem — ex: "lembrete de agendamento no dia seguinte, tom amigável"'
                            successMessage="Mensagem gerada! Revise o texto antes de salvar."
                            onGenerated={(conteudoGerado) => setTemplate(prev => ({ ...prev, conteudo: conteudoGerado }))}
                        />
                        <div className="editor-toolbar">
                            <button
                                type="button"
                                onClick={() => setShowVariables(!showVariables)}
                                className="action-button action-button-secondary"
                            >
                                <ClipboardCopy size={16} className="icon" /> Inserir Variável
                                <ChevronDown size={16} className={`transition-transform ${showVariables ? 'rotate-180' : ''}`}/>
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMode(viewMode === 'preview' ? 'editor' : 'preview')}
                                className="action-button action-button-secondary"
                            >
                                <Eye size={16} className="icon" /> {viewMode === 'preview' ? 'Voltar ao Editor' : 'Preview'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMode(viewMode === 'source' ? 'editor' : 'source')}
                                className="action-button action-button-secondary"
                            >
                                <Code size={16} className="icon" /> {viewMode === 'source' ? 'Editor Visual' : 'Código HTML'}
                            </button>
                        </div>

                        {showVariables && <PlaceholdersPanel onInsert={insertPlaceholder} />}

                        <div className="editor-container">
                            {viewMode === 'editor' && (
                                <CKEditor
                                    editor={ClassicEditor}
                                    data={template.conteudo || ''}
                                    onChange={onCKEditorChange}
                                    onReady={onEditorReady}
                                    config={{
                                        toolbar: [ 'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote', '|', 'undo', 'redo' ]
                                    }}
                                    key={current ? current._id : 'new-template'}
                                />
                            )}
                            {viewMode === 'preview' && (
                                <div
                                    className="editor-preview prose max-w-none"
                                    dangerouslySetInnerHTML={{ __html: template.conteudo || '' }}
                                />
                            )}
                            {viewMode === 'source' && (
                                <textarea
                                    ref={sourceEditorRef}
                                    name="conteudo"
                                    value={template.conteudo || ''}
                                    onChange={onChange}
                                    rows="15"
                                    className="editor-source-textarea"
                                />
                            )}
                        </div>
                        {errors.conteudo && <p className="text-red-500 text-xs mt-1">{errors.conteudo}</p>}

                        <small className="form-help">
                            Use chaves duplas com o nome da variável (ex: {'{{'}cliente.nomeCompleto{'}}'}) para criar placeholders.
                        </small>
                    </fieldset>

                    <fieldset className="form-section">
                        <legend className="form-section-title">Período de Envio Padrão</legend>
                        <p className="form-help" style={{ marginBottom: '0.75rem' }}>
                            Sugestão de quando este template deve ser enviado — pode ser sobrescrita
                            ao usar o template numa Comunicação Automática ou num envio manual pontual.
                        </p>
                        <PeriodoEnvioFields
                            value={template.periodoEnvioPadrao}
                            onChange={onPeriodoEnvioChange}
                            permitirAntesDepois
                            idPrefix="template-periodo-envio"
                        />
                    </fieldset>

                    <fieldset className="form-section">
                        <legend className="form-section-title">Configurações</legend>
                        <div className="form-group-checkbox">
                            <input
                                type="checkbox"
                                name="ativo"
                                id="ativo"
                                checked={template.ativo}
                                onChange={onCheckboxChange}
                                className="form-checkbox"
                            />
                            <label htmlFor="ativo">Template ativo</label>
                        </div>
                        
                        {isMatrizAdmin && (
                            <div className="form-group-checkbox mt-4">
                                <input 
                                    type="checkbox" 
                                    name="isPadrao" 
                                    id="isPadrao" 
                                    checked={template.isPadrao} 
                                    onChange={onCheckboxChange} 
                                    className="form-checkbox"
                                />
                                <label htmlFor="isPadrao">Template Padrão Global (visível para todas as contas/franquias)</label>
                            </div>
                        )}

                        {isMatrizAdmin && (
                            <div className="form-group-checkbox mt-4">
                                <input
                                    type="checkbox"
                                    name="restritoMatriz"
                                    id="restritoMatriz"
                                    checked={template.restritoMatriz}
                                    onChange={onCheckboxChange}
                                    className="form-checkbox"
                                />
                                <label htmlFor="restritoMatriz">Exclusivo da Matriz (mensagem da ferramenta — esqueci senha, conta criada, etc. — nunca visível para contas/franquias)</label>
                            </div>
                        )}
                    </fieldset>

                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="action-button action-button-secondary"
                        >
                            <XCircle size={18} className="icon" />
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={isMutating}
                            className="action-button action-button-primary"
                        >
                            {isMutating ? (
                                <Loader2 className="animate-spin icon" />
                            ) : (
                                <Save size={18} className="icon" />
                            )}
                            {current ? 'Atualizar Template' : 'Salvar Template'}
                        </button>
                    </div>
        </form>
    );
};

TemplateMensagemForm.propTypes = {
    current: PropTypes.object,
    onCloseForm: PropTypes.func.isRequired,
    canalSelecionado: PropTypes.string,
    canalOptions: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
        })
    ),
};

TemplateMensagemForm.defaultProps = {
    current: null,
    canalSelecionado: '',
    canalOptions: [],
};

export default TemplateMensagemForm;